import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchLeads, updateLead } from "../../store/lead"
import LeadDetails from "../ui/LeadDetails"
import AdminLeadsChart from "../ui/AdminLeadsChart"
import ReactPaginate from "react-paginate"
import {
  ArrowBigLeftDashIcon,
  ArrowBigRightDashIcon,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react"
import Spinner from "../Spinner"
import { formatPhoneNumber } from "../../util"
import "../styles/AdminLeadsPage.css"

function AdminLeadsPage() {
  const dispatch = useDispatch()
  const { leads } = useSelector((state) => state.lead)
  const [selectedLead, setSelectedLead] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [isEditing, setIsEditing] = useState(false)
  const [expandedLeads, setExpandedLeads] = useState(new Set())
  const itemsPerPage = 10

  useEffect(() => {
    dispatch(fetchLeads())
  }, [dispatch])

  const createNewLead = () => {
    setSelectedLead({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      zipCode: "",
      message: "",
      service: "",
      preferredAppointmentDateTime: "",
      initialFollowUp: false,
      jobQuoteCreated: false,
      invoiceSent: false,
      contractSigned: false,
      jobCompleted: false,
      jobPaid: false,
      openBalance: false,
    })
    setIsEditing(true)
  }

  const toggleLeadExpansion = (leadId) => {
    setExpandedLeads((prevExpandedLeads) => {
      const newExpandedLeads = new Set(prevExpandedLeads)
      if (newExpandedLeads.has(leadId)) {
        newExpandedLeads.delete(leadId)
      } else {
        newExpandedLeads.add(leadId)
      }
      return newExpandedLeads
    })
  }

  const handlePageClick = (event) => {
    setCurrentPage(event.selected + 1)
  }

  const handleRowClick = (lead) => {
    setSelectedLead(lead)
    setIsEditing(true)
  }

  const handleSaveChanges = (updatedLead) => {
    dispatch(updateLead({ id: updatedLead.id, updatedData: updatedLead }))
    setSelectedLead(updatedLead)
    setIsEditing(false)
  }

  const handleCancelEdit = () => {
    setSelectedLead(null)
    setIsEditing(false)
  }

  const startIndex = (currentPage - 1) * itemsPerPage
  const selectedLeads = leads.slice(startIndex, startIndex + itemsPerPage)
  const pageCount = Math.ceil(leads.length / itemsPerPage)

  return (
    <div className="adminLeadsPageContainer">
      <h1 className="adminLeadsPageHeader">Leads</h1>
      <AdminLeadsChart />
      <h1 className="adminLeadsPageHeader">Leads Management</h1>
      <div className="adminLeadsTableWrapper">
        {leads.length ? (
          <table className="adminLeadsTable">
            <thead>
              <tr>
                <th>ID</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Address</th>
                <th>City</th>
                <th>Zip Code</th>
                <th>Service</th>
                <th>Appt. Date</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {selectedLeads.map((lead) => {
                const isExpanded = expandedLeads.has(lead.message)
                const words = lead.message.split(" ")
                const shouldTruncate = words.length > 3
                const formattedPhone = formatPhoneNumber(lead.phone)
                const displayedMessage =
                  isExpanded || !shouldTruncate
                    ? lead.message
                    : words.slice(0, 3).join(" ") + "..."

                return (
                  <tr
                    key={lead.id}
                    onClick={() => handleRowClick(lead)}
                    className="singleLeadLine"
                  >
                    <td>{lead.id}</td>
                    <td>{lead.firstName}</td>
                    <td>{lead.lastName}</td>
                    <td>{lead.email}</td>
                    <td>{formattedPhone}</td>
                    <td>{lead.address}</td>
                    <td>{lead.city}</td>
                    <td>{lead.zipCode}</td>
                    <td>{lead.service}</td>
                    <td>
                      {lead.preferredAppointmentDateTime
                        ? new Date(
                            lead.preferredAppointmentDateTime
                          ).toLocaleString()
                        : "N/A"}
                    </td>
                    <td>
                      <span>{displayedMessage}</span>
                      {shouldTruncate && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleLeadExpansion(lead.message)
                          }}
                          className="readMoreButton"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronsLeft size={20} />
                              <span>Read Less</span>
                            </>
                          ) : (
                            <>
                              <span>Read More</span>
                              <ChevronsRight size={20} />
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        ) : (
          <Spinner />
        )}
        <ReactPaginate
          previousLabel={<ArrowBigLeftDashIcon size={24} />}
          nextLabel={<ArrowBigRightDashIcon size={24} />}
          pageCount={pageCount}
          onPageChange={handlePageClick}
          containerClassName="pagination"
          activeClassName="active"
        />
      </div>
      {selectedLead ? (
        <LeadDetails
          lead={selectedLead}
          onSave={handleSaveChanges}
          onCancel={handleCancelEdit}
          isEditable={isEditing}
          setIsEditable={setIsEditing}
        />
      ) : (
        <div className="leadDetails-noLead">
          <p>No Lead Selected</p>
          <button onClick={createNewLead}>Create New Lead</button>
        </div>
      )}
    </div>
  )
}

export default AdminLeadsPage
